// ════════════════════════════════════════════════════════════════════════
//  Vergabe von Schlüsseln
//
//  Ein Schlüssel ist der Name, unter dem der Fortschritt eines Kindes
//  gespeichert wird. Er entsteht einmal, wandert mit seiner Lektion oder Stufe
//  mit – gleich wohin sie verschoben, wie sie umbenannt, in welcher Fassung sie
//  geändert wird – und ist nach dem ersten Veröffentlichen unveränderlich.
//
//  Deshalb steht in ihm NICHTS, was sich ändern kann: keine Nummer
//  (`stufe-1` – nach dem ersten Umsortieren falsch), kein Buchabschnitt
//  (`grundreihe-4a` – falsch, sobald der Lehrgang vom Heft abweicht) und auch
//  kein Titel (`zeigefinger-f-und-j` – falsch, sobald die Redaktion ihn in einer
//  neuen Fassung schärfer formuliert). Alle drei Formen hat dieses Projekt
//  nacheinander gehabt; jede war eine Aussage, die die Wirklichkeit später
//  widerlegt hat und die man trotzdem nie korrigieren durfte.
//
//  Was übrig bleibt, ist ein Zufallsschlüssel: `l-7bd4mz01`, `s-3k9f2xqa`. Er
//  sagt nichts – und kann deshalb nichts Falsches sagen. Was ein Mensch lesen
//  will, steht in Titel und Untertitel, und die darf man ändern.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "@otto/lehrgang";

/** Kleinbuchstaben und Ziffern – mehr lässt das Schlüsselschema nicht zu. */
const ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const LENGTH = 8;

function id(prefix: string, random: () => number): string {
  let out = "";
  for (let i = 0; i < LENGTH; i++) {
    out += ALPHABET[Math.floor(random() * ALPHABET.length)];
  }
  return `${prefix}-${out}`;
}

/**
 * Ein freier Schlüssel.
 *
 * Geprüft wird gegen ALLE vergebenen – Lektionen wie Stufen, Entwurf wie
 * veröffentlichte Fassung. Ein veröffentlichter Schlüssel zählt auch dann noch,
 * wenn seine Stufe im Entwurf gerade nicht zu sehen ist: an ihm hängt
 * Fortschritt, und erben darf ihn nichts.
 */
function free(prefix: string, taken: ReadonlySet<string>, random: () => number): string {
  for (let tries = 0; tries < 100; tries++) {
    const candidate = id(prefix, random);
    if (!taken.has(candidate)) return candidate;
  }
  throw new Error("Kein freier Schlüssel zu finden");
}

/** Alle Schlüssel eines Bundles – Lektionen, Stufen und der Test. */
export function keysOf(bundle: Bundle | null): Set<string> {
  const keys = new Set<string>();
  for (const lesson of bundle?.lessons ?? []) {
    keys.add(lesson.key);
    for (const stage of lesson.stages) keys.add(stage.key);
  }
  // Der Test hängt an derselben Art Schlüssel wie eine Stufe: an ihm hängen die
  // Messungen der Kinder, und geerbt werden darf er ebenso wenig.
  if (bundle?.assessment) keys.add(bundle.assessment.key);
  return keys;
}

/**
 * Ein neuer Lektionsschlüssel.
 *
 * `random` ist ein Argument, damit ein Test den Zufall festhalten kann; im
 * Betrieb ist es `Math.random`.
 */
export function newLessonKey(
  bundle: Bundle | null,
  published: ReadonlySet<string> = new Set(),
  random: () => number = Math.random,
): string {
  return free("l", new Set([...keysOf(bundle), ...published]), random);
}

/** Ein neuer Stufenschlüssel – unabhängig von der Lektion, in der sie liegt. */
export function newStageKey(
  bundle: Bundle | null,
  published: ReadonlySet<string> = new Set(),
  random: () => number = Math.random,
): string {
  return free("s", new Set([...keysOf(bundle), ...published]), random);
}

/** Ein neuer Testschlüssel. Der Test ist keine Stufe, also hat er ein eigenes Zeichen. */
export function newAssessmentKey(
  bundle: Bundle | null,
  published: ReadonlySet<string> = new Set(),
  random: () => number = Math.random,
): string {
  return free("t", new Set([...keysOf(bundle), ...published]), random);
}
